import React from "react";

import { Grid } from "@material-ui/core";
import { useFormikContext } from "formik";
import { useSelector } from "react-redux";

import { FormikAwareBusinessPICSelector } from "components/Form/FormikControls/BusinessPICSelector";
import { Row } from "components/Layout";
import { Text } from "components/Text";

import { BUYER, VENDOR } from "constants/businesses";
import { SecondaryButton } from "components/Form";
import { SplitType } from "constants/splits";

import { formatSplitAmount } from "lib/splits";

import { getBusinessById } from "selectors";

import { MobileTransactionalCreateSaleFormValues } from "..";
import { withNamespace } from "components/Form/FormikControls/lib";

import {
  useHasBuyerSplitPermission,
  useHasVendorSplitPermission,
  useToggle,
} from "hooks";
import {
  AddVendorSplitDialog,
  PendingVendorSplit,
} from "../Splits/AddVendorSplitDialog";
import {
  AddBuyerSplitDialog,
  PendingBuyerSplit,
} from "../Splits/AddBuyerSplitDialog";
import Big from "big.js";

interface SplitAllocationRowProps {
  businessId: string;
  amount: string | number;
  type: SplitType;
  gstOverride?: boolean | null;
}

const SplitAllocationRow = ({
  businessId,
  amount,
  type,
  gstOverride,
}: SplitAllocationRowProps) => {
  const business = useSelector(getBusinessById(businessId));
  const amountLabel = formatSplitAmount(new Big(amount), type);
  const gstLabel =
    gstOverride === true ? " · GST" : gstOverride === false ? " · No GST" : "";
  return (
    <Grid item xs={6}>
      <Text>
        {business?.name ?? businessId}: {amountLabel}
        {gstLabel}
      </Text>
    </Grid>
  );
};

interface WhoTabProps {
  saleLotNamespace: string;
  consignmentNamespace: string;
  vendorSplitNamespace: string;
  buyerSplitNamespace: string;
}

export const WhoTab = ({
  saleLotNamespace,
  consignmentNamespace,
  vendorSplitNamespace,
  buyerSplitNamespace,
}: WhoTabProps) => {
  const { values, setFieldValue } =
    useFormikContext<MobileTransactionalCreateSaleFormValues>();
  const {
    consignment: { vendor_id: vendorId },
    saleLot: { buyer_id: buyerId, quantity },
    vendorSplit,
    buyerSplit,
  } = values;

  const [isVendorSplitDialogOpen, toggleIsVendorSplitDialogOpen] =
    useToggle(false);
  const [isBuyerSplitDialogOpen, toggleIsBuyerSplitDialogOpen] =
    useToggle(false);

  const handleSaveVendorSplit = (split: PendingVendorSplit) => {
    setFieldValue(vendorSplitNamespace, split);
  };

  const handleSaveBuyerSplit = (split: PendingBuyerSplit) => {
    setFieldValue(buyerSplitNamespace, split);
  };

  const hasBuyerSplitPermission = useHasBuyerSplitPermission();
  const hasVendorSplitPermission = useHasVendorSplitPermission();

  return (
    <Grid item container xs={12}>
      <Row fullWidth marginVertical={2}>
        <FormikAwareBusinessPICSelector
          label="Vendor"
          subLabel="Search by Business Name or PIC"
          businessFieldName={withNamespace(consignmentNamespace, "vendor_id")}
          propertyFieldName={withNamespace(
            consignmentNamespace,
            "vendor_property_id",
          )}
          businessRoles={[VENDOR]}
          labelFontSize="charlie"
          subLabelFontSize="beta"
        />
      </Row>

      {hasVendorSplitPermission &&
        vendorSplit?.splits.map((s, i) => (
          <Row fullWidth key={i}>
            <SplitAllocationRow
              businessId={s.businessId}
              amount={s.amount}
              type={vendorSplit.type}
              gstOverride={s.shouldChargeGSTOverride}
            />
          </Row>
        ))}

      {hasVendorSplitPermission && (
        <Row fullWidth marginVertical={2} flex justifyEnd>
          <Grid item xs={6} container spacing={2}>
            <SecondaryButton
              disabled={!vendorId}
              style={{ width: "100%" }}
              data-tour="add-vendor-split"
              type="button"
              onClick={toggleIsVendorSplitDialogOpen}
            >
              {vendorSplit?.splits.length ? "Edit" : "Add"} Split
            </SecondaryButton>
          </Grid>
        </Row>
      )}

      <Row fullWidth marginVertical={2}>
        <FormikAwareBusinessPICSelector
          label="Buyer"
          subLabel="Search by Business Name or PIC"
          businessFieldName={withNamespace(saleLotNamespace, "buyer_id")}
          propertyFieldName={withNamespace(
            saleLotNamespace,
            "destination_property_id",
          )}
          businessRoles={[BUYER]}
          labelFontSize="charlie"
          subLabelFontSize="beta"
        />
      </Row>

      {hasBuyerSplitPermission &&
        buyerSplit?.splits.map((s, i) => (
          <Row fullWidth key={i}>
            <SplitAllocationRow
              businessId={s.businessId}
              amount={s.amount}
              type={buyerSplit.type}
            />
          </Row>
        ))}

      {hasBuyerSplitPermission && (
        <Row fullWidth marginVertical={2} flex justifyEnd>
          <Grid item xs={6} container spacing={2}>
            <SecondaryButton
              disabled={!buyerId}
              style={{ width: "100%" }}
              data-tour="add-buyer-split"
              type="button"
              onClick={toggleIsBuyerSplitDialogOpen}
            >
              {buyerSplit?.splits.length ? "Edit" : "Add"} Split
            </SecondaryButton>
          </Grid>
        </Row>
      )}

      {isVendorSplitDialogOpen && vendorId && (
        <AddVendorSplitDialog
          primaryBusinessId={vendorId}
          quantity={quantity}
          initialSplit={vendorSplit}
          onSave={handleSaveVendorSplit}
          onClose={toggleIsVendorSplitDialogOpen}
        />
      )}

      {isBuyerSplitDialogOpen && buyerId && (
        <AddBuyerSplitDialog
          primaryBusinessId={buyerId}
          quantity={quantity}
          initialSplit={buyerSplit}
          onSave={handleSaveBuyerSplit}
          onClose={toggleIsBuyerSplitDialogOpen}
        />
      )}
    </Grid>
  );
};
